<template>
	<SelectableCard
		:is-selected="isSelected"
		:data-testid="dataTestId"
		:disabled="disabled"
		:aria-label="title"
		role="option"
		@select="handleToggle"
	>
		<div class="flex items-start">
			<!-- Visual checkbox indicator -->
			<div
				class="mt-1 h-4 w-4 border-2 rounded flex items-center justify-center flex-shrink-0"
				:class="[
					isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 bg-white',
					disabled ? 'opacity-50' : '',
				]"
			>
				<svg v-if="isSelected" class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
					<path
						fill-rule="evenodd"
						d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
						clip-rule="evenodd"
					/>
				</svg>
			</div>

			<!-- Content -->
			<div class="ml-3 flex-1">
				<div
					class="text-sm"
					:class="[
						isSelected ? 'text-gray-900 font-medium' : 'text-gray-700',
						disabled ? 'opacity-50' : '',
					]"
				>
					<div class="font-medium">
						{{ title }}
					</div>
					<div v-if="description" class="text-sm text-gray-500 mt-1">
						{{ description }}
					</div>
					<slot name="content" />
				</div>
			</div>
		</div>
	</SelectableCard>
</template>

<script setup lang="ts">
/**
 * SelectableCheckboxCard Component
 *
 * A reusable component that combines SelectableCard with checkbox functionality.
 * Follows SOLID principles:
 * - Single Responsibility: Handles checkbox card selection only
 * - Open/Closed: Extensible via slots and props, closed for modification
 * - Liskov Substitution: Can be used anywhere a selectable card is needed
 * - Interface Segregation: Clean, focused props interface
 * - Dependency Inversion: Depends on abstractions (SelectableCard)
 *
 * Features:
 * - Consistent DOM structure and styling
 * - Proper accessibility support
 * - Event handling with proper propagation control
 * - Extensible via slots for custom content
 * - TypeScript support with proper interfaces
 * - Disabled state support
 * - Customizable test IDs for E2E testing
 */
import SelectableCard from './SelectableCard.vue';

export interface SelectableCheckboxCardProps {
	id: string;
	title: string;
	description?: string;
	isSelected: boolean;
	disabled?: boolean;
	dataTestId?: string;
}

const props = withDefaults(defineProps<SelectableCheckboxCardProps>(), {
	disabled: false,
	dataTestId: '',
});

const emit = defineEmits<{
	toggle: [id: string];
}>();

// Event handlers following single responsibility principle
const handleToggle = () => {
	if (!props.disabled) {
		emit('toggle', props.id);
	}
};
</script>
